/**
 * Promo code utilities for Vortixy marketing campaigns.
 *
 * Codes follow the format: VORTIXY-XXXXXXXX
 * where X is an uppercase alphanumeric character (no ambiguous chars like 0/O, 1/I/L).
 *
 * This module is safe to import from client components. Code generation, which
 * relies on Node's `crypto`, lives in `promo-codes-server.ts` to keep the
 * crypto polyfill out of client bundles.
 */

const CHARSET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const CODE_LENGTH = 8;
export const PREFIX = "VORTIXY-";

/** Characters that are easy to confuse when reading aloud or in screenshots. */
export const SAFE_CHARSET = CHARSET.replace(/[OI]/g, "");

/**
 * Validate the format of a promo code (client-side).
 * Returns true if the code matches the expected format.
 */
export function isValidPromoCodeFormat(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  if (!normalized.startsWith(PREFIX)) return false;
  const suffix = normalized.slice(PREFIX.length);
  if (suffix.length !== CODE_LENGTH) return false;
  return /^[A-Z2-9]+$/.test(suffix);
}

/**
 * Normalize a promo code for display/comparison.
 */
export function normalizePromoCode(code: string): string {
  return code.trim().toUpperCase();
}

export function getPromoCodeDisplayPlan(plan: string): string {
  if (plan === "maxplus") return "Max+";
  if (plan === "max") return "Max";
  if (plan === "pro") return "Pro";
  return plan;
}

export function getPromoCodeDurationLabel(days: number): string {
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  if (days === 30) return "1 month";
  if (days < 365) return `${Math.round(days / 30)} months`;
  if (days === 365) return "1 year";
  return `${Math.round(days / 365)} years`;
}
